import React from 'react';
import LocationEditMachine from './locationeditmachine';
import Machine from '../domain/machine';
import { Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Add from '@mui/icons-material/Add';
import HighlightBox from '../common/highlightbox';
import AddIcon from '@mui/icons-material/Add';
import Filter from '../domain/filter';
import ObjectIdGenerator from '../modules/objectidgenerator';
import Logger from '../common/logger';
import { getProductPricingDataBatch } from '../modules/pricingcachedata';

const PlasticBagProductName = 'PLASTICBAG 1050x1050 DT';

//
// Params
// - machines
// - filterProducts
// - machineChanged
// - machineAdded
// - machineRemoved
export default function LocationEditMachines(props) {
    const { t } = useTranslation();

    const onClickNewMachine = () => {
        Machine.NewObject()
            .then(function (machine) {
                machine.viewOrder = props.machines.length + 1;
                props.machineAdded(machine);
            })
            .catch(function (err) {
                throw new Error('Failed to create object');
            });
    };

    const machineChanged = (machine) => {
        props.machineChanged(machine);
    };

    const machineRemoved = (machine) => {
        props.machineRemoved(machine);
    };

    const findMachine = (id) => {
        for (let m of props.machines) {
            if (m.id === id) return m;
        }
        return null;
    };

    const sortUp = (id) => {
        let m = findMachine(id);
        if (m != null) {
            if (m.viewOrder === 1) return;
            else {
                let prev = props.machines[m.viewOrder - 2];
                prev.viewOrder++;
                m.viewOrder--;
                props.machineChanged(m);
                props.machineChanged(prev);
            }
        }
    };

    const sortDown = (id) => {
        let m = findMachine(id);
        if (m != null) {
            if (m.viewOrder === props.machines.length) return;
            else {
                let next = props.machines[m.viewOrder];
                next.viewOrder--;
                m.viewOrder++;
                props.machineChanged(m);
                props.machineChanged(next);
            }
        }
    };

    const replaceProduct = async (oldProductId, newProductId, filterId, machineId, replaceType) => {
        await props.replaceProduct(oldProductId, newProductId, filterId, machineId, replaceType);
    };

    const findMachinesFromSurveyNotFoundInCurrentMachines = (survey, machines) => {
        const results = [];
        if (!survey || !survey.machineRooms) return results;
        const currentMachines = machines || [];
        for (const room of survey.machineRooms) {
            if (!room || !room.machines) continue;
            for (const surveyMachine of room.machines) {
                const found = currentMachines.find((cm) => cm.surveyMachineUid === surveyMachine.id);
                if (!found) results.push({ ...surveyMachine, room: room.name });
            }
        }
        return results;
    };

    const getMachineFromSurvey = (machineUid) => {
        if (props.survey && props.showSurveyInfos) {
            // Find machine from survey data
            if (!props.survey.machineRooms) return null;
            for (let r of props.survey.machineRooms) {
                if (!r.machines) return null;
                for (let m of r.machines) {
                    if (m.id === machineUid) return { ...m, room: r.name };
                }
            }
        }
        return null;
    };

    const getPotentialFilterInfos = (machineUid) => {
        if (!props.allFilterInfos) return [];
        return props.allFilterInfos.filter((info) => info.machineUid === machineUid);
    };

    const getGasketProduct = (selectedSeal) => {
        if (props.filterProducts) {
            const gaskets = props.filterProducts
                .filter((p) => p.productName && p.productName.startsWith('VG GASKET'))
                .map((p) => ({ ...p, productName: p.productName.replace(/ /g, '').replace(/,/g, '.') }));
            const searchTerm = selectedSeal.replace(/ /g, '').replace(/,/g, '.');
            return gaskets.find((p) => p.productName === searchTerm);
        }
        return null;
    };

    const getRecyclingBagProduct = () => {
        if (props.filterProducts) {
            return props.filterProducts.find((p) => p.productName === PlasticBagProductName);
        }
        return null;
    };

    const addSurveyMachine = async (surveyMachine) => {
        let idsNeeded = 1;
        const machine = new Machine();
        machine.viewOrder = props.machines.length + 1;
        machine.name = surveyMachine.name;
        machine.airVolume = surveyMachine.airVolume;
        machine.machineLocation = surveyMachine.room;
        machine.machineArea = surveyMachine.serviceArea;
        machine.surveyMachineUid = surveyMachine.id;
        machine.filterInfos = getPotentialFilterInfos(surveyMachine.id);
        machine.filters = [];

        let gasketProd = null;
        let plasticBagProd = null;
        let prodInfoToGet = [];
        if (surveyMachine.selectedSeal && surveyMachine.numberOfSeals) {
            idsNeeded++;
            gasketProd = getGasketProduct(surveyMachine.selectedSeal);
            if (!gasketProd) {
                Logger.LogError(`Cannot find gasket, selectedSeal:${surveyMachine.selectedSeal}`, '');
                throw new Error('Error');
            }
            prodInfoToGet.push(gasketProd.storageId);
        }
        if (surveyMachine.recyclingBags) {
            idsNeeded++;
            plasticBagProd = getRecyclingBagProduct();
            if (!plasticBagProd) {
                Logger.LogError(`Cannot find plastic bag ${PlasticBagProductName}`, '');
                throw new Error('Error');
            }
            prodInfoToGet.push(plasticBagProd.storageId);
        }
        let objId = (await ObjectIdGenerator.newIdMultiple(idsNeeded)).first;
        machine.id = objId++;
        const pricingInfos = prodInfoToGet.length
            ? await getProductPricingDataBatch(props.contractId ? props.contractId : 0, prodInfoToGet)
            : [];

        if (surveyMachine.selectedSeal && surveyMachine.numberOfSeals) {
            const priceInfo = pricingInfos.find((p) => p.productClusterId === gasketProd.storageId);
            machine.filters.push(
                new Filter({
                    id: objId++,
                    count: surveyMachine.numberOfSeals,
                    product: gasketProd,
                    discount: 0,
                    viewOrder: 99,
                    price: null,
                    contractPricesInUse: priceInfo.contractPricesInUse,
                    unitContractNetPrice: priceInfo.discountedPrice,
                })
            );
        }
        if (surveyMachine.recyclingBags) {
            const priceInfo = pricingInfos.find((p) => p.productClusterId === plasticBagProd.storageId);
            machine.filters.push(
                new Filter({
                    id: objId++,
                    count: surveyMachine.recyclingBags,
                    product: plasticBagProd,
                    discount: 0,
                    viewOrder: 99,
                    price: null,
                    contractPricesInUse: priceInfo.contractPricesInUse,
                    unitContractNetPrice: priceInfo.discountedPrice,
                })
            );
        }

        props.machineAdded(machine);
    };

    const missingMachines =
        props.survey && props.machines && props.showSurveyInfos
            ? findMachinesFromSurveyNotFoundInCurrentMachines(props.survey, props.machines)
            : [];

    if (props.machines.length > 0)
        return (
            <div>
                {props.machines.map((machine) => (
                    <LocationEditMachine
                        key={machine.id}
                        machine={machine}
                        filterProducts={props.filterProducts}
                        productBundles={props.productBundles}
                        machineChanged={machineChanged}
                        machineRemoved={machineRemoved}
                        sortUp={sortUp}
                        sortDown={sortDown}
                        getFilterDiscount={props.getFilterDiscount}
                        setFilterDiscount={props.setFilterDiscount}
                        swapDiscountPrice={props.swapDiscountPrice}
                        setFilterPrice={props.setFilterPrice}
                        getFilterPrice={props.getFilterPrice}
                        expand={props.expandDetails}
                        contractId={props.contractId}
                        replaceProduct={replaceProduct}
                        showcaseMode={props.showcaseMode}
                        fromSurvey={props.fromSurvey}
                        showSurveyInfos={props.showSurveyInfos}
                        machineFromSurvey={getMachineFromSurvey(machine.surveyMachineUid)}
                        currency={props.currency}
                        exchangeRate={props.exchangeRate}
                    />
                ))}
                <br />
                {missingMachines.map((machine) => (
                    <HighlightBox severity="info" key={machine.id}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={10}>
                                {machine.name} {machine.room} {machine.serviceArea}
                            </Grid>
                            <Grid item xs={2} container justifyContent="flex-end" alignItems="center">
                                <Button
                                    name="add-machine-from-survey"
                                    onClick={() => addSurveyMachine(machine)}
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    color="primary">
                                    {t('buttons.add')}
                                </Button>
                            </Grid>
                        </Grid>
                    </HighlightBox>
                ))}
                <hr style={{ marginTop: '0' }} />
                <Button
                    variant="contained"
                    color="primary"
                    name="add-new-machine"
                    startIcon={<Add />}
                    onClick={onClickNewMachine}>
                    {t('location.machine')}
                </Button>
            </div>
        );
    else
        return (
            <div>
                <Button
                    variant="contained"
                    color="primary"
                    name="add-new-machine"
                    startIcon={<Add />}
                    onClick={onClickNewMachine}>
                    {t('location.machine')}
                </Button>
            </div>
        );
}
